import { GridCell, GridCellKind, GridColumn } from "@glideapps/glide-data-grid";

import { ColumnModel, UploadExcelResult } from "api/data";

import { defaultUwiHeaders } from "../UserDefinedColumnEditWizard";

// Native Excel error tokens the server stores as "n/a"; matching is trimmed and
// case-insensitive against the whole cell text.
export const EXCEL_ERROR_TOKENS = [
  "#DIV/0!",
  "#N/A",
  "#VALUE!",
  "#REF!",
  "#NUM!",
  "#NAME?",
  "#NULL!",
  "#GETTING_DATA"
];

// Typed NA-family values the server also stores as "n/a"; matched against the whole cell
// text after trimming, case-insensitively. Deliberately excludes dotted/hyphenated
// variants ("n.a.", "n.a", "n-a"), "null", "None", "-", and "nan" -- those display
// verbatim.
export const TYPED_NA_VALUES = ["na", "n/a"];

// Flagged (error-bearing) columns get a soft, slightly-transparent red header (matching the
// import issues panel) with plain black text — a quiet marker rather than a solid fill.
const FLAGGED_HEADER_BG = "#fff1f0";
const FLAGGED_HEADER_TEXT = "#000000";

// Original error tokens are drawn in red while "Show original values" is on, so the user
// can see exactly which cells the "n/a" normalization changed.
const ORIGINAL_ERROR_TEXT_COLOR = "#f5222d";

export function isExcelErrorToken(text: string): boolean {
  const trimmed = (text ?? "").trim().toLowerCase();
  return EXCEL_ERROR_TOKENS.some((token) => token.toLowerCase() === trimmed);
}

/**
 * Counts data cells holding a genuine Excel error token that the server actually folds to
 * "n/a" — the cells the issues panel reports as "N cells with errors". Restricted to the
 * columns the backend flagged in columnsWithErrors, so unnamed columns (never imported) and
 * columns the backend did not flag are excluded, matching the stored data. Typed na/n/a
 * folds are also excluded. When `includedHeaderNames` is provided, only those columns are
 * counted (used to drop columns the user has de-selected from Column Select).
 */
export function countErrorCells(
  data:
    | Pick<UploadExcelResult, "rawDataColumns" | "headerRowIndex" | "columnsWithErrors">
    | null
    | undefined,
  includedHeaderNames?: Nullable<Set<string>>
): number {
  if (!data?.rawDataColumns) return 0;
  const flagged = new Set(data.columnsWithErrors ?? []);
  if (flagged.size === 0) return 0;
  const headerRow = data.headerRowIndex ?? 0;
  let count = 0;
  data.rawDataColumns.forEach((column) => {
    if (column.isKeyHeader || !flagged.has(column.headerName)) return;
    if (includedHeaderNames && !includedHeaderNames.has(column.headerName)) return;
    column.data.forEach((cell, rowIndex) => {
      if (rowIndex > headerRow && isExcelErrorToken(cell)) count += 1;
    });
  });
  return count;
}

/**
 * 0-based data-row indices (aligned to `uwis`, i.e. after the header rows) that contain at
 * least one genuine Excel error token in a backend-flagged column — the rows the "show rows
 * with errors" filter isolates. Restricted to columns in columnsWithErrors, the same set
 * countErrorCells uses, so the filter and the "N cells with errors" count stay consistent
 * (typed na/n/a folds are excluded). When `includedHeaderNames` is provided, only those
 * columns are considered (matching the de-selection applied to the count).
 */
export function errorDataRowIndices(
  data:
    | Pick<
        UploadExcelResult,
        "rawDataColumns" | "headerRowIndex" | "uwis" | "columnsWithErrors"
      >
    | null
    | undefined,
  includedHeaderNames?: Nullable<Set<string>>
): number[] {
  if (!data?.rawDataColumns || !data.uwis) return [];
  const flagged = new Set(data.columnsWithErrors ?? []);
  if (flagged.size === 0) return [];
  const headerRow = data.headerRowIndex ?? 0;
  const result: number[] = [];
  for (let i = 0; i < data.uwis.length; i++) {
    const cellIndex = i + headerRow + 1;
    const hasError = data.rawDataColumns.some(
      (column) =>
        !column.isKeyHeader &&
        flagged.has(column.headerName) &&
        (!includedHeaderNames || includedHeaderNames.has(column.headerName)) &&
        isExcelErrorToken(column.data[cellIndex] ?? "")
    );
    if (hasError) result.push(i);
  }
  return result;
}

// Display-only counterpart of the server's stored-data normalization: returns "n/a" when
// the whole trimmed cell text is an Excel error token or a typed NA-family value, otherwise
// returns the text unchanged. Never used for submitted data -- the server already
// normalizes what it stores.
export function normalizeDisplayValue(text: string): string {
  const trimmed = (text ?? "").trim().toLowerCase();
  return isExcelErrorToken(text) || TYPED_NA_VALUES.includes(trimmed) ? "n/a" : text;
}

// columnsWithErrors entries match columns[].name / rawDataColumns[].headerName exactly
// (post-dedup); the UI never re-derives error status by scanning cells.
function isFlaggedColumnName(name: string, flaggedNames: string[]): boolean {
  return Boolean(name) && flaggedNames.includes(name);
}

// Grid column titles may carry a "Name (units)" suffix; flaggedNames never do. Matching
// requires the title to equal the flagged name exactly, or to equal the flagged column's
// OWN name+units combination -- a prefix check alone would also match an unrelated column
// whose literal name happens to start with "<flaggedName> (", e.g. a distinct "GOR (raw)"
// column when "GOR" is flagged.
function titleMatchesFlaggedColumn(
  title: string,
  flaggedNames: string[],
  dataColumns: ColumnModel[]
): boolean {
  return flaggedNames.some((name) => {
    if (title === name) return true;
    const units = dataColumns.find((col) => col.name === name)?.units?.trim();
    return Boolean(units) && title === `${name} (${units})`;
  });
}

export function getData(
  [col, row]: readonly [number, number],
  dataRef: React.MutableRefObject<UploadExcelResult>,
  selectedColumns: string[],
  filteredRowIndices?: number[] | null,
  entityKind?: string
): GridCell {
  const data = dataRef.current;
  if (!data || !data.uwis || !data.columns) {
    return {
      kind: GridCellKind.Text,
      data: "",
      displayData: "",
      allowOverlay: false
    };
  }

  let actualRowIndex = row;
  if (filteredRowIndices && filteredRowIndices.length > 0) {
    if (row >= filteredRowIndices.length) {
      return {
        kind: GridCellKind.Text,
        data: "",
        displayData: "",
        allowOverlay: false
      };
    }
    actualRowIndex = filteredRowIndices[row];
  }

  if (actualRowIndex >= data.uwis.length || col >= data.columns.length + 1) {
    return {
      kind: GridCellKind.Text,
      data: "",
      displayData: "",
      allowOverlay: false
    };
  }

  // If first column, return UWI
  if (col === 0) {
    const uwiValue = data.uwis[actualRowIndex];

    // Check if this is an invalid UWI/Facility ID
    const isInvalidUwi =
      (entityKind === "Well" && uwiValue === "Invalid UWI") ||
      (entityKind === "Facility" && uwiValue === "Invalid Facility ID") ||
      uwiValue === undefined ||
      uwiValue === "";

    return {
      kind: GridCellKind.Text,
      data: uwiValue,
      displayData: uwiValue,
      allowOverlay: false,
      themeOverride: {
        bgCell: isInvalidUwi ? "#ff4d4f" : "#00c7b1",
        bgHeader: isInvalidUwi ? "#ff4d4f" : "#00c7b1",
        textDark: "#ffffff"
      }
    };
  }

  // Determine the columns to use (all columns if selectedColumns is empty)
  const columnsToUse =
    selectedColumns.length > 0
      ? data.columns.filter((column) => {
          const units = column.units?.trim();
          const nameWithUnits = units ? `${column.name} (${units})` : column.name;
          return (
            selectedColumns.includes(column.name) ||
            selectedColumns.includes(nameWithUnits)
          );
        })
      : data.columns;

  // Map the column index to the columnsToUse array
  const columnDef = columnsToUse[col - 1];
  const columnData = columnDef?.data[data.uwis[actualRowIndex]] || "";

  return {
    kind: GridCellKind.Text,
    data: columnData,
    displayData: columnData,
    allowOverlay: false
  };
}

export interface RawPreviewColumnMeta {
  /** Index into rawDataColumns. */
  rawIndex: number;
  headerName: string;
  isUnnamed: boolean;
}

// Order of the raw preview grid's non-key columns (grid column 0 is always the key
// column). Blank-header columns stay in rawDataColumns even after the header-naming step
// resolves, so they are excluded from the grid's column mapping — or later named
// columns shift into their slot — UNLESS their rawDataColumns index is explicitly
// requested: while the naming step is active the wizard requests the data-bearing unnamed
// columns so they can be named in place.
export function getRawPreviewColumnOrder(
  data: UploadExcelResult,
  selectedColumns: string[],
  unnamedIndicesToShow?: Nullable<number[]>
): RawPreviewColumnMeta[] {
  if (!data?.rawDataColumns) return [];
  const selectedKeyHeader =
    data.rawDataColumns.find((c) => c.isKeyHeader)?.headerName || "UWI";
  const unnamedToShow = new Set(unnamedIndicesToShow ?? []);
  const metas: RawPreviewColumnMeta[] = [];
  data.rawDataColumns.forEach((column, rawIndex) => {
    if (column.isKeyHeader) return;
    if (column.headerName === "") {
      if (unnamedToShow.has(rawIndex)) {
        metas.push({ rawIndex, headerName: "", isUnnamed: true });
      }
      return;
    }
    if (
      defaultUwiHeaders.includes(column.headerName) &&
      column.headerName !== selectedKeyHeader
    ) {
      return;
    }
    if (selectedColumns.length > 0 && !selectedColumns.includes(column.headerName)) {
      return;
    }
    metas.push({ rawIndex, headerName: column.headerName, isUnnamed: false });
  });
  return metas;
}

export function getRawData(
  [col, row]: readonly [number, number],
  dataRef: React.MutableRefObject<UploadExcelResult>,
  selectedColumns: string[],
  filteredRowIndices?: number[] | null,
  entityKind?: string,
  precomputedColumnOrder?: Nullable<RawPreviewColumnMeta[]>,
  showActualValues?: boolean
): GridCell {
  const data = dataRef.current;
  if (!data || !data.rawDataColumns || !data.rawDataColumns.length) {
    return {
      kind: GridCellKind.Text,
      data: "",
      displayData: "",
      allowOverlay: false
    };
  }

  const headerRow = data?.headerRowIndex ?? 0;

  let actualRowIndex = row;
  if (filteredRowIndices && filteredRowIndices.length > 0) {
    if (row >= filteredRowIndices.length) {
      return {
        kind: GridCellKind.Text,
        data: "",
        displayData: "",
        allowOverlay: false
      };
    }
    actualRowIndex = filteredRowIndices[row];
  }

  // If row or column index is out of bounds, return an empty cell
  if (
    actualRowIndex >= data.rawDataColumns[0].data.length ||
    col >= data.rawDataColumns.length
  ) {
    return {
      kind: GridCellKind.Text,
      data: "",
      displayData: "",
      allowOverlay: false
    };
  }

  // If first column, return data from the key header column
  if (col === 0) {
    const keyHeaderColumn = data.rawDataColumns.find((column) => column.isKeyHeader);
    const columnData = keyHeaderColumn?.data[actualRowIndex] || "";

    let isInvalidUwi = false;
    if (actualRowIndex > headerRow && data.uwis) {
      const uwiIndex = actualRowIndex - headerRow - 1;
      if (uwiIndex >= 0 && uwiIndex < data.uwis.length) {
        const processedUwi = data.uwis[uwiIndex];
        isInvalidUwi =
          (entityKind === "Well" && processedUwi === "Invalid UWI") ||
          (entityKind === "Facility" && processedUwi === "Invalid Facility ID") ||
          processedUwi === undefined ||
          processedUwi === "";
      }
    }

    let bgColor = "#00c7b1";
    let textColor = "#ffffff";

    if (actualRowIndex >= headerRow) {
      if (isInvalidUwi) {
        bgColor = "#ff4d4f";
        textColor = "#ffffff";
      } else {
        bgColor = "#00c7b1";
        textColor = "#ffffff";
      }
    } else {
      bgColor = "#00c7b1";
      textColor = "#ffffff";
    }

    return {
      kind: GridCellKind.Text,
      data: columnData,
      displayData: columnData,
      allowOverlay: false,
      themeOverride:
        actualRowIndex >= headerRow
          ? {
              bgCell: bgColor,
              bgHeader: bgColor,
              textDark: textColor,
              baseFontStyle: actualRowIndex === headerRow ? "bold 13px" : "normal 13px"
            }
          : null
    };
  }

  // Map the column index through the shared grid-column ordering. The caller passes the
  // memoized order so cell access stays O(1) instead of rescanning every column per cell.
  const columnOrder =
    precomputedColumnOrder ?? getRawPreviewColumnOrder(data, selectedColumns);
  const columnMeta = columnOrder[col - 1];
  const columnDef =
    columnMeta !== undefined ? data.rawDataColumns[columnMeta.rawIndex] : undefined;
  const columnData = columnDef?.data[actualRowIndex] || "";
  const flaggedNames = data.columnsWithErrors ?? [];
  const isFlagged = columnDef && isFlaggedColumnName(columnDef.headerName, flaggedNames);

  let themeOverride = null;
  if (actualRowIndex === headerRow) {
    themeOverride = {
      bgCell: isFlagged ? FLAGGED_HEADER_BG : "#f7f7f8",
      textDark: isFlagged ? FLAGGED_HEADER_TEXT : "#000000",
      baseFontStyle: "bold 13px"
    };
  } else if (actualRowIndex > headerRow && isExcelErrorToken(columnData)) {
    // Tint cells that were folded from a genuine Excel error token so the user can spot the
    // actual error cells at a glance (and pinpoint them once the "show rows with errors"
    // filter is on). Only genuine Excel error tokens are tinted — typed na/n/a folds render
    // as a plain "n/a".
    themeOverride = { textDark: ORIGINAL_ERROR_TEXT_COLOR };
  }

  // Data rows render the server-normalized "n/a" for error tokens and typed NA values,
  // unless the user opts to see the original wire values. The header row and pre-header rows
  // are never normalized.
  const displayValue =
    actualRowIndex > headerRow && !showActualValues
      ? normalizeDisplayValue(columnData)
      : columnData;

  return {
    kind: GridCellKind.Text,
    data: displayValue,
    displayData: displayValue,
    allowOverlay: false,
    themeOverride
  };
}

export function getStyledColumns(
  columns: GridColumn[],
  flaggedNames: string[] = [],
  dataColumns: ColumnModel[] = []
): GridColumn[] {
  return columns.map((col, index) => {
    const isFlagged =
      index !== 0 &&
      titleMatchesFlaggedColumn(String(col.title ?? ""), flaggedNames, dataColumns);
    return {
      ...col,
      themeOverride: {
        bgHeader: index === 0 ? "#00c7b1" : isFlagged ? FLAGGED_HEADER_BG : "#f7f7f8",
        textHeader: index === 0 ? "#ffffff" : isFlagged ? FLAGGED_HEADER_TEXT : "#000000"
      }
    };
  });
}
